import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { PayableApprovalReviewActions } from "../components/PayableApprovalReviewActions"
import { PayableApprovalStatusCell } from "../components/PayableApprovalStatusCell"

import StyledChip from "@/components/common/Chip/Chip"
import type { Invoice } from "@/graphql/codegen/graphql"
import {
  INVOICE_PAYMENT_METHOD_LABELS,
  INVOICE_TYPE_LABELS,
} from "@/screens/Accounting/subfeatures/Invoices/constants/labels"
import { calculateInvoiceExpenseTotal } from "@/screens/Accounting/subfeatures/Invoices/utils"
import { formatCurrencyWithCents } from "@/utils/currency"
import { formatDate } from "@/utils/date"
import { formatUserName } from "@/utils/user"
import { booleanToYesNoString } from "@/utils/utils"

interface UsePayableApprovalColumnsProps {
  onReview?: (row: Invoice) => void
}

type UsePayableApprovalColumnsReturn = ColumnDef<Invoice>[]

export const usePayableApprovalColumns = ({
  onReview,
}: UsePayableApprovalColumnsProps = {}): UsePayableApprovalColumnsReturn => {
  return useMemo<ColumnDef<Invoice>[]>(
    () => [
      {
        accessorKey: "vendorInvoiceNumber",
        header: "Vendor Invoice #",
        cell: ({ row }) => row.original.invoiceNumber,
        enableSorting: true,
        size: 140,
      },
      {
        accessorKey: "vendorName",
        header: "Vendor",
        cell: ({ row }) => row.original.vendorCompany?.name ?? "",
        enableSorting: true,
        size: 180,
      },
      {
        accessorKey: "invoiceType",
        header: "Type",
        cell: ({ row }) => (
          <StyledChip
            label={INVOICE_TYPE_LABELS[row.original.invoiceType]}
            className='bg-primary-200 text-gray-900'
            size='small'
          />
        ),
        enableSorting: true,
        size: 90,
      },
      {
        accessorKey: "dateEntered",
        header: "Date Entered",
        cell: ({ row }) => (row.original.createdOn ? formatDate(row.original.createdOn) : ""),
        enableSorting: true,
        size: 120,
      },
      {
        accessorKey: "dueDate",
        header: "Due Date",
        cell: ({ row }) => (row.original.dueDate ? formatDate(row.original.dueDate) : ""),
        enableSorting: true,
        size: 110,
      },
      {
        accessorKey: "paymentMethod",
        header: "Payment Type",
        cell: ({ row }) => INVOICE_PAYMENT_METHOD_LABELS[row.original.paymentMethod],
        enableSorting: true,
        size: 120,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ row }) => (
          <span className='tabular-nums'>{formatCurrencyWithCents(calculateInvoiceExpenseTotal(row.original))}</span>
        ),
        enableSorting: true,
        size: 120,
      },
      {
        accessorKey: "printedCheck",
        header: "Printed Check",
        cell: ({ row }) => (row.original.printedCheck ? "Yes" : "No"),
        enableSorting: true,
        size: 120,
      },
      {
        accessorKey: "enteredEditedBy",
        header: "Entered / Edited By",
        cell: ({ row }) => formatUserName(row.original.updatedByUser ?? row.original.createdByUser, true),
        enableSorting: true,
        size: 150,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <PayableApprovalStatusCell invoice={row.original} />,
        enableSorting: true,
        size: 130,
      },
      {
        accessorKey: "duplicateWarningBypassed",
        header: "Duplicate Warning Bypassed",
        cell: ({ row }) => booleanToYesNoString(row.original.duplicateWarningBypassed),
        enableSorting: true,
        size: 180,
      },
      {
        id: "actions",
        header: "",
        size: 160,
        enableSorting: false,
        cell: ({ row }) => <PayableApprovalReviewActions row={row} onReview={onReview} />,
      },
    ],
    [onReview]
  )
}
